import type { AdminQueryError } from "../../contracts/admin-query-errors";
import { isAdminQueryError, isAdminQueryErrorCode } from "../../contracts/admin-query-errors";

type HttpErrorEnvelope = {
  error?: {
    code?: unknown;
    message?: unknown;
    requestId?: unknown;
  };
};

type HttpLikeError = {
  status?: unknown;
  requestId?: unknown;
  body?: unknown;
  cause?: unknown;
  message?: unknown;
};

function readRequestId(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function mapHttpStatusToCode(status: number | undefined): AdminQueryError["code"] {
  if (status === 401 || status === 403) {
    return "ADMIN_QUERY_PERMISSION_DENIED";
  }

  if (status === 400 || status === 422) {
    return "ADMIN_QUERY_INVALID_FILTER";
  }

  if (status === 404) {
    return "ADMIN_QUERY_USER_NOT_FOUND";
  }

  if (status === 409 || status === 412) {
    return "ADMIN_QUERY_PROJECTION_STALE";
  }

  if (status === 502 || status === 503 || status === 504) {
    return "ADMIN_QUERY_SOURCE_UNAVAILABLE";
  }

  return "ADMIN_QUERY_INTERNAL_ERROR";
}

function parseEnvelopeError(
  error: HttpErrorEnvelope | undefined,
  status: number | undefined,
): AdminQueryError | undefined {
  const envelopeError = error?.error;

  if (envelopeError === undefined) {
    return undefined;
  }

  if (isAdminQueryErrorCode(envelopeError.code) && typeof envelopeError.message === "string") {
    return {
      code: envelopeError.code,
      message: envelopeError.message,
      requestId: readRequestId(envelopeError.requestId),
    };
  }

  if (envelopeError.code === "ROUTE_NOT_FOUND") {
    return {
      code: "ADMIN_QUERY_SOURCE_UNAVAILABLE",
      message:
        typeof envelopeError.message === "string" && envelopeError.message.length > 0
          ? envelopeError.message
          : "Admin API route is not deployed or is currently unavailable.",
      requestId: readRequestId(envelopeError.requestId),
    };
  }

  const mappedCode = mapHttpStatusToCode(status);
  if (mappedCode !== "ADMIN_QUERY_INTERNAL_ERROR") {
    return {
      code: mappedCode,
      message:
        typeof envelopeError.message === "string" && envelopeError.message.length > 0
          ? envelopeError.message
          : "Admin query request failed.",
      requestId: readRequestId(envelopeError.requestId),
    };
  }

  return {
    code: "ADMIN_QUERY_INTERNAL_ERROR",
    message: "Admin query error envelope is invalid.",
    requestId: readRequestId(envelopeError.requestId),
  };
}

export function normalizeAdminQueryError(error: unknown): AdminQueryError {
  if (isAdminQueryError(error)) {
    return error;
  }

  if (typeof error === "object" && error !== null) {
    const httpLikeError = error as HttpLikeError;
    const status =
      typeof httpLikeError.status === "number" ? httpLikeError.status : undefined;
    const envelopeError = parseEnvelopeError(
      httpLikeError.body as HttpErrorEnvelope | undefined,
      status,
    );

    if (envelopeError !== undefined) {
      return {
        ...envelopeError,
        cause: error,
      };
    }

    const message =
      typeof httpLikeError.message === "string" && httpLikeError.message.length > 0
        ? httpLikeError.message
        : "Admin query request failed.";

    return {
      code: mapHttpStatusToCode(status),
      message,
      requestId: readRequestId(httpLikeError.requestId),
      cause: httpLikeError.cause ?? error,
    };
  }

  if (error instanceof Error) {
    return {
      code: "ADMIN_QUERY_INTERNAL_ERROR",
      message: error.message || "Admin query request failed.",
      cause: error,
    };
  }

  return {
    code: "ADMIN_QUERY_INTERNAL_ERROR",
    message: "Unknown admin query error.",
    cause: error,
  };
}
